import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHashtags } from '../../hooks/apiHooks/searchHooks'
import { analytics } from '../../utils/analytics'

interface SuggestionProps {
  onClick: (q?: string) => void
}

let prevTimestamp = 0

const Suggestions: React.FC<SuggestionProps> = ({ onClick }) => {
  const { data: hashtags, isLoading } = useHashtags()
  const [isPaused, setIsPaused] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const [dragStart, setDragStart] = useState({ x: 0, translateX: 0 })
  const [translateX, setTranslateX] = useState(0)
  const [containerWidth, setContainerWidth] = useState(0)
  const [contentWidth, setContentWidth] = useState(0)

  const hasActuallyDragged = useRef(false)
  const animationRef = useRef<number>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  // Measure container and content dimensions
  useEffect(() => {
    if (!hashtags?.data.length) return

    const measureDimensions = () => {
      if (containerRef.current && contentRef.current) {
        setContainerWidth(containerRef.current.offsetWidth)
        setContentWidth(contentRef.current.offsetWidth)
      }
    }

    measureDimensions()
    window.addEventListener('resize', measureDimensions)
    return () => window.removeEventListener('resize', measureDimensions)
  }, [hashtags])

  // Calculate drag bounds
  const getClampedTranslateX = (value: number) => {
    const minTranslateX = -(contentWidth - containerWidth)
    const maxTranslateX = 0
    return Math.max(minTranslateX, Math.min(maxTranslateX, value))
  }

  // Auto-scroll animation - just keep moving left forever
  useEffect(() => {
    if (isPaused || isDragging) {
      return
    }

    const animate = (timestamp: number) => {
      if (prevTimestamp === 0) prevTimestamp = timestamp
      const delta = (timestamp - prevTimestamp) / 1000
      prevTimestamp = timestamp

      const speedPerSecond = 40 // px/sec

      const contentWidth = contentRef?.current?.offsetWidth || -1
      setTranslateX(prev => {
        if (contentWidth > 0 && prev <= -contentWidth + containerWidth) {
          return 0
        }
        return prev - speedPerSecond * delta
      })
      animationRef.current = requestAnimationFrame(animate)
    }

    animationRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationRef.current) {
        prevTimestamp = 0
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isPaused, isDragging, hashtags])

  // Global mouse event handlers for dragging
  useEffect(() => {
    if (!isDragging) {
      // Restore user-select when not dragging
      document.body.style.userSelect = ''
      document.body.style.webkitUserSelect = ''
      return
    }

    // Prevent selection on the whole page during drag (Safari)
    document.body.style.userSelect = 'none'
    document.body.style.webkitUserSelect = 'none'

    const handleGlobalMouseMove = (e: MouseEvent) => {
      e.preventDefault()

      const deltaX = e.clientX - dragStart.x
      const distance = Math.abs(deltaX)

      if (distance > 5) {
        hasActuallyDragged.current = true
      }

      const newTranslateX = dragStart.translateX + deltaX
      setTranslateX(getClampedTranslateX(newTranslateX))
    }

    const handleGlobalMouseUp = () => {
      setIsDragging(false)
      setTimeout(() => {
        hasActuallyDragged.current = false
      }, 100)
    }

    document.addEventListener('mousemove', handleGlobalMouseMove)
    document.addEventListener('mouseup', handleGlobalMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove)
      document.removeEventListener('mouseup', handleGlobalMouseUp)
      // Restore user-select when drag ends
      document.body.style.userSelect = ''
      document.body.style.webkitUserSelect = ''
    }
  }, [isDragging, dragStart, getClampedTranslateX])

  // Mouse drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    hasActuallyDragged.current = false
    setIsPaused(true)

    setDragStart({
      x: e.clientX,
      translateX: translateX,
    })
  }

  // Touch drag handlers for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true)
    hasActuallyDragged.current = false
    setIsPaused(true)
    setDragStart({
      x: e.touches[0].clientX,
      translateX: translateX,
    })
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return

    e.preventDefault()

    const deltaX = e.touches[0].clientX - dragStart.x
    const distance = Math.abs(deltaX)

    if (distance > 5) {
      hasActuallyDragged.current = true
    }

    const newTranslateX = dragStart.translateX + deltaX
    setTranslateX(getClampedTranslateX(newTranslateX))
  }

  const handleTouchEnd = () => {
    setIsDragging(false)
    setTimeout(() => {
      setIsPaused(false)
      hasActuallyDragged.current = false
    }, 100)
  }

  const logEvent = (label: string) => {
    analytics.logEvent('aa_question_suggested_tags_clicked', {
      tag: label,
    })
  }

  const handleMouseLeave = () => {
    setIsPaused(false)
  }

  const handleTagClick = (label: string) => {
    if (hasActuallyDragged.current) {
      return
    }
    logEvent(label)
    onClick(label)
    setIsPaused(true)
  }

  // Create many copies of tags for truly infinite scroll
  const renderTags = useMemo(() => {
    if (!hashtags?.data.length) return []
    const copies = []
    // Render 10 copies - enough for any reasonable scroll distance
    for (let copy = 0; copy < 10; copy++) {
      copies.push(
        ...hashtags.data.map((tag, index) => (
          <div
            key={`${tag.label}-${copy}-${index}`}
            onClick={() => {
              handleTagClick(tag.label)
            }}
            className="flex gap-2 h-8 lg:h-10 text-xs md:text-sm rounded-lg tc-mv-blur align-center items-center text-white px-4 whitespace-nowrap"
          >
            {tag.label}
          </div>
        )),
      )
    }
    return copies
  }, [hashtags])

  if (isLoading)
    return (
      <div className="flex gap-2 mt-2 overflow-hidden">
        {new Array(4).fill(0).map((_, index) => (
          <div
            key={index}
            className="flex flex-grow-0 flex-shrink-0 w-[130px] gap-2 h-8 lg:h-10 rounded-lg tc-mv-blur animate-pulse"
          ></div>
        ))}
      </div>
    )

  if (!hashtags?.data.length) return null

  return (
    <div className="w-full mt-2 tc-mv-hash-wrap">
      <div
        ref={containerRef}
        className="overflow-hidden cursor-grab select-none"
        style={{
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
        onMouseDown={handleMouseDown}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          ref={contentRef}
          className="flex gap-2 w-max"
          style={{
            transform: `translateX(${translateX}px)`,
            transition: isDragging ? 'none' : 'none',
          }}
        >
          {renderTags}
        </div>
      </div>
    </div>
  )
}

export default Suggestions
